'use client'

import { useEffect, useRef } from 'react'
import {
  trackBlogPostAberto,
  trackBlogScrollProfundidade,
  trackBlogCtaClicado,
} from '@/lib/analytics'

interface BlogTrackingProps {
  slug: string
  category: string
}

export function BlogTracking({ slug, category }: BlogTrackingProps) {
  const trackedDepths = useRef<Set<number>>(new Set())

  useEffect(() => {
    trackBlogPostAberto(slug, category)
  }, [slug, category])

  useEffect(() => {
    const article = document.querySelector('.blog-content')
    if (!article) return

    const depths = [25, 50, 75, 100] as const
    const observers: IntersectionObserver[] = []

    depths.forEach((depth) => {
      const target = document.createElement('div')
      target.setAttribute('data-scroll-depth', String(depth))
      target.style.height = '1px'
      target.style.width = '100%'

      const position = depth / 100
      const children = Array.from(article.children)
      const insertIndex = Math.floor(children.length * position)

      if (insertIndex < children.length) {
        article.insertBefore(target, children[insertIndex])
      } else {
        article.appendChild(target)
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !trackedDepths.current.has(depth)) {
              trackedDepths.current.add(depth)
              trackBlogScrollProfundidade(slug, depth)
            }
          })
        },
        { threshold: 0 }
      )

      observer.observe(target)
      observers.push(observer)
    })

    // Track CTA clicks within blog content
    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement
      const link = target.closest('a')
      if (link) {
        const href = link.getAttribute('href') || ''
        if (href.includes('calculadora') || href.includes('/calculadoras')) {
          trackBlogCtaClicado(slug, href)
        }
      }
    }

    article.addEventListener('click', handleClick)

    return () => {
      observers.forEach((o) => o.disconnect())
      article.removeEventListener('click', handleClick)
    }
  }, [slug])

  return null
}

interface BlogCTAButtonProps {
  slug: string
  href: string
  children: React.ReactNode
  className?: string
}

export function BlogCTAButton({ slug, href, children, className }: BlogCTAButtonProps) {
  const handleClick = () => {
    trackBlogCtaClicado(slug, href)
  }

  return (
    <a href={href} onClick={handleClick} className={className}>
      {children}
    </a>
  )
}
